/*
This serves two functions:
1. Record every action and state transition in the SPA. This is useful for debugging.
2. Send every error to Sentry. This allows us to guarantee that we record every error
   from the storage adapter, and then by the Client.
*/
import { trpc } from "@client/trpc";
import {
  actionMessage,
  unconfirmedActionMessage,
  type ActionMessage,
  type Reducer,
  type UnconfirmedActionMessage,
} from "@engine/core";
import type { ActionError, ClientStorageAdapter } from "@engine/network";
import type { RoundAggregate } from "@engine/round/aggregate";
import * as Sentry from "@sentry/react";
import Dexie from "dexie";
import { NumberOrRange } from "server/routes/getAggregateActionsByVersion";
import { z } from "zod";
import { SPAStorageAdapter } from "./SPAStorageAdapter";

const _actionRecord = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("construct"),
    aggregateId: z.string(),
    userProfileHash: z.string(),
    lastConfirmedAggVersion: z.number(),
  }),
  z.object({
    type: z.literal("performUnconfirmedAction"),
    action: unconfirmedActionMessage,
    error: z.instanceof(Error).optional(),
  }),
  z.object({
    type: z.literal("performConfirmedAction"),
    action: actionMessage,
    error: z.instanceof(Error).optional(),
    actionErrors: z
      .array(
        z.object({
          actionId: z.string(),
          error: z.instanceof(Error),
        }),
      )
      .optional(),
  }),
]);

type ActionRecord = z.infer<typeof _actionRecord>;

export default class SPAStorageAdapterWrapper implements ClientStorageAdapter {
  storageAdapter: SPAStorageAdapter;
  tape: ActionRecord[];

  constructor(storageAdapter: SPAStorageAdapter) {
    this.storageAdapter = storageAdapter;
    this.tape = [
      {
        type: "construct",
        aggregateId: this.storageAdapter.aggregateId,
        userProfileHash: this.storageAdapter.userProfileHash,
        lastConfirmedAggVersion: this.storageAdapter.getLastConfirmedVersion(),
      },
    ];
    if (typeof window !== "undefined") {
      window["getSpaState"] = () => this._getState(true);
      window["getTape"] = () => this.tape;
      window["getAggregate"] = () =>
        trpc().getAggregate.query({
          roundId: this.storageAdapter.aggregateId,
        });
    }
  }

  pushTape(actionRecord: ActionRecord) {
    this.tape.push(actionRecord);
    if (this.tape.length > 100) {
      this.tape.shift();
    }
  }

  async clearUnconfirmedActions() {
    await this.storageAdapter.clearUnconfirmedActions();
  }

  async _getState(full: boolean = false) {
    const adapter = this.storageAdapter;
    const db = adapter.db;

    const state = {
      tape: this.tape,
      aggregateId: adapter.aggregateId,
      userProfileHash: adapter.userProfileHash,
      currentUnconfirmedAggVersion: adapter.currentUnconfirmedAgg.version,
      snapshotCount: adapter.confirmedSnapshotsSinceLoad.length,
      unconfirmedActions: await adapter.getUnconfirmedActions(),
      storageEstimate: undefined as StorageEstimate | undefined,
    };

    if (
      typeof window !== "undefined" &&
      window.navigator &&
      navigator.storage &&
      navigator.storage.estimate
    ) {
      state.storageEstimate = await navigator.storage.estimate();
    }

    const confirmedActions = adapter.confirmedActionsSinceLoad;

    const unconfirmedActions = db.unconfirmedActions2
      .where("aggregateId")
      .equals(adapter.aggregateId);

    if (full) {
      return {
        ...state,
        confirmedActions,
        unconfirmedActions: await unconfirmedActions.toArray(),
        lastConfirmedAgg: adapter.lastConfirmedAgg,
      };
    } else {
      return {
        ...state,
        confirmedActionCount: confirmedActions.length,
        unconfirmedActionCount: await unconfirmedActions.count(),
        lastConfirmedAgg: adapter.lastConfirmedAgg.version,
      };
    }
  }

  async captureException(error: Error) {
    Sentry.setContext("state", await this._getState());

    if (navigator.storage && navigator.storage.estimate) {
      const estimation = await navigator.storage.estimate();
      Sentry.setContext("storageEstimate", {
        quota: estimation.quota,
        usage: estimation.usage,
      });
    }

    if (error instanceof Dexie.AbortError) {
      Sentry.setContext("outerError", {
        message: error.message,
        stack: error.stack,
      });
      Sentry.captureException(error.inner);
    } else {
      Sentry.captureException(error);
    }
  }

  static async init(
    reducer: Reducer,
    aggregateId: string,
    userProfileHash: string,
    downloadRound: (aggregateId: string) => Promise<RoundAggregate>,
    downloadActionsById: (
      aggregateId: string,
      ids: string[],
    ) => Promise<{ [id: string]: ActionMessage }>,
    downloadActionsByVersion: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: ActionMessage }>,
  ): Promise<SPAStorageAdapterWrapper> {
    try {
      const innerStorageAdapter = await SPAStorageAdapter.init(
        reducer,
        aggregateId,
        userProfileHash,
        downloadRound,
        downloadActionsById,
        downloadActionsByVersion,
      );
      return new SPAStorageAdapterWrapper(innerStorageAdapter);
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async performUnconfirmedAction(
    reducer: Reducer,
    action: UnconfirmedActionMessage,
  ): Promise<null | Error> {
    let error: Error | undefined;
    const result = await this.storageAdapter.performUnconfirmedAction(
      reducer,
      action,
    );
    if (result instanceof Error) {
      await this.captureException(result);
      error = result;
    }
    this.pushTape({
      type: "performUnconfirmedAction",
      action,
      error,
    });
    return result;
  }

  async performConfirmedAction(
    reducer: Reducer,
    action: ActionMessage,
  ): Promise<void | Error | ActionError[]> {
    let error: Error | undefined;
    let actionErrors: ActionError[] | undefined;
    const result = await this.storageAdapter.performConfirmedAction(
      reducer,
      action,
    );
    if (result instanceof Error) {
      await this.captureException(result);
      error = result;
    } else {
      actionErrors = result;
    }
    this.pushTape({
      type: "performConfirmedAction",
      action,
      error,
      actionErrors,
    });
    return result;
  }

  getConfirmedActions(): ActionMessage[] {
    return this.storageAdapter.getConfirmedActions();
  }

  getAction(actionId: string): Promise<UnconfirmedActionMessage | null> {
    return this.storageAdapter.getAction(actionId);
  }

  getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null> {
    return this.storageAdapter.getAggregateAtVersion(reducer, version);
  }

  getUnconfirmedAggregateSync(): RoundAggregate {
    return this.storageAdapter.getUnconfirmedAggregateSync();
  }

  getConfirmedAggregateSync(): RoundAggregate {
    return this.storageAdapter.getConfirmedAggregateSync();
  }

  getLastConfirmedVersion(): number {
    return this.storageAdapter.getLastConfirmedVersion();
  }

  getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: UnconfirmedActionMessage;
    }[]
  > {
    return this.storageAdapter.getUnconfirmedActions();
  }

  checkConsistency(): Promise<void> {
    return this.storageAdapter.checkConsistency();
  }

  getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error> {
    return this.storageAdapter.getAggregateBeforeUnconfirmedAction(
      reducer,
      actionId,
    );
  }
}
